<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { renderTemplatePreview } from '@chatwoot/utils';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Label from 'dashboard/components-next/label/Label.vue';
import TemplateBodyEditor from './TemplateBodyEditor.vue';
import TemplateButtonsEditor from './TemplateButtonsEditor.vue';
import WhatsappTemplatesAPI from 'dashboard/api/whatsappTemplates';

const props = defineProps({
  inboxId: { type: [String, Number], required: true },
});

const emit = defineEmits(['created']);

const { t } = useI18n();

const dialogRef = ref(null);
const isSubmitting = ref(false);
const submitError = ref('');

const name = ref('');
const language = ref('en_US');
const category = ref('UTILITY');
const body = ref('');
const headerFormat = ref('NONE');
const headerText = ref('');
const footer = ref('');
const buttons = ref([]);

const CATEGORY_OPTIONS = [
  {
    value: 'MARKETING',
    label: t('WHATSAPP_TEMPLATES.BUILDER.CATEGORY.MARKETING'),
  },
  { value: 'UTILITY', label: t('WHATSAPP_TEMPLATES.BUILDER.CATEGORY.UTILITY') },
  {
    value: 'AUTHENTICATION',
    label: t('WHATSAPP_TEMPLATES.BUILDER.CATEGORY.AUTHENTICATION'),
  },
];

// Meta only supports a media (IMAGE/VIDEO/DOCUMENT) header via a separate
// resumable-upload flow this release does not implement - offering those
// options here would let the user configure something the submit call
// cannot actually satisfy (see TemplateManagementService's honesty
// requirement). TEXT is the only header type Phase 1 can genuinely submit.
const HEADER_FORMAT_OPTIONS = [
  { value: 'NONE', label: t('WHATSAPP_TEMPLATES.BUILDER.HEADER.NONE') },
  { value: 'TEXT', label: t('WHATSAPP_TEMPLATES.BUILDER.HEADER.TEXT') },
];

const NAME_FORMAT = /^[a-z0-9_]+$/;
// Mirrors Whatsapp::TemplateValidator - a client-side pre-check only. The
// server independently re-validates every one of these; this never
// replaces that, it only avoids a pointless round trip for obvious errors.
const validationErrors = computed(() => {
  const errors = [];
  if (!name.value.trim())
    errors.push(t('WHATSAPP_TEMPLATES.BUILDER.VALIDATION.NAME_REQUIRED'));
  else if (!NAME_FORMAT.test(name.value))
    errors.push(t('WHATSAPP_TEMPLATES.BUILDER.VALIDATION.NAME_FORMAT'));
  if (!language.value.trim())
    errors.push(t('WHATSAPP_TEMPLATES.BUILDER.VALIDATION.LANGUAGE_REQUIRED'));
  if (!body.value.trim())
    errors.push(t('WHATSAPP_TEMPLATES.BUILDER.VALIDATION.BODY_REQUIRED'));
  if (body.value.length > 1024)
    errors.push(t('WHATSAPP_TEMPLATES.BUILDER.VALIDATION.BODY_TOO_LONG'));
  if (headerFormat.value === 'TEXT' && !headerText.value.trim()) {
    errors.push(
      t('WHATSAPP_TEMPLATES.BUILDER.VALIDATION.HEADER_TEXT_REQUIRED')
    );
  }
  buttons.value.forEach((btn, index) => {
    if (!btn.text?.trim())
      errors.push(
        t('WHATSAPP_TEMPLATES.BUILDER.VALIDATION.BUTTON_TEXT_REQUIRED', {
          index: index + 1,
        })
      );
    if (btn.type === 'URL' && !btn.url?.trim())
      errors.push(
        t('WHATSAPP_TEMPLATES.BUILDER.VALIDATION.BUTTON_URL_REQUIRED', {
          index: index + 1,
        })
      );
  });
  return errors;
});

// Sample values purely for the live preview (never sent to Meta) - the
// same substitution renderTemplatePreview already does for
// WhatsAppTemplateParser.vue's send-time preview, so authoring and sending
// render identically for the same template data.
const previewValues = computed(() => {
  const matches = body.value.match(/{{([^}]+)}}/g) || [];
  return matches.reduce((acc, token) => {
    const key = token.replace(/{{|}}/g, '').trim();
    acc[key] = t('WHATSAPP_TEMPLATES.BUILDER.PREVIEW.SAMPLE_VALUE', { n: key });
    return acc;
  }, {});
});

const previewBody = computed(() =>
  renderTemplatePreview(body.value, previewValues.value)
);

const resetForm = () => {
  name.value = '';
  language.value = 'en_US';
  category.value = 'UTILITY';
  body.value = '';
  headerFormat.value = 'NONE';
  headerText.value = '';
  footer.value = '';
  buttons.value = [];
  submitError.value = '';
};

const buildPayload = () => {
  const payload = {
    name: name.value.trim(),
    language: language.value.trim(),
    category: category.value,
    body: body.value,
  };
  if (footer.value.trim()) payload.footer = footer.value.trim();
  if (headerFormat.value === 'TEXT')
    payload.header = { format: 'TEXT', text: headerText.value.trim() };
  if (buttons.value.length) {
    payload.buttons = buttons.value.map(btn => ({
      type: btn.type,
      text: btn.text,
      ...(btn.type === 'URL' ? { url: btn.url } : {}),
    }));
  }
  return payload;
};

const submit = async () => {
  if (validationErrors.value.length) return;

  isSubmitting.value = true;
  submitError.value = '';
  try {
    await WhatsappTemplatesAPI.create(props.inboxId, buildPayload());
    useAlert(t('WHATSAPP_TEMPLATES.BUILDER.SUBMIT_SUCCESS'));
    emit('created');
    resetForm();
    dialogRef.value?.close();
  } catch (error) {
    // Backend never leaks internal details here - error.response is the
    // controller's structured { error, details } (or Meta's own real
    // failure surfaced cleanly), safe to show as-is.
    submitError.value =
      error?.response?.data?.error ||
      error?.response?.data?.errors?.join(', ') ||
      t('WHATSAPP_TEMPLATES.BUILDER.SUBMIT_ERROR');
  } finally {
    isSubmitting.value = false;
  }
};

const open = () => {
  resetForm();
  dialogRef.value?.open();
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="3xl"
    overflow-y-auto
    :show-confirm-button="false"
    :show-cancel-button="false"
  >
    <div class="flex flex-col w-full gap-5">
      <h3 class="text-lg font-medium text-n-slate-12">
        {{ t('WHATSAPP_TEMPLATES.BUILDER.TITLE') }}
      </h3>

      <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <!-- Builder -->
        <div class="flex flex-col gap-4">
          <Input
            v-model="name"
            :label="t('WHATSAPP_TEMPLATES.BUILDER.NAME_LABEL')"
            :placeholder="t('WHATSAPP_TEMPLATES.BUILDER.NAME_PLACEHOLDER')"
          />
          <Input
            v-model="language"
            :label="t('WHATSAPP_TEMPLATES.BUILDER.LANGUAGE_LABEL')"
            :placeholder="t('WHATSAPP_TEMPLATES.BUILDER.LANGUAGE_PLACEHOLDER')"
          />
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('WHATSAPP_TEMPLATES.BUILDER.CATEGORY_LABEL') }}
            </label>
            <ComboBox v-model="category" :options="CATEGORY_OPTIONS" />
          </div>

          <TemplateBodyEditor v-model:body="body" />

          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('WHATSAPP_TEMPLATES.BUILDER.HEADER.LABEL') }}
            </label>
            <ComboBox v-model="headerFormat" :options="HEADER_FORMAT_OPTIONS" />
            <Input
              v-if="headerFormat === 'TEXT'"
              v-model="headerText"
              class="mt-1"
              :placeholder="
                t('WHATSAPP_TEMPLATES.BUILDER.HEADER.TEXT_PLACEHOLDER')
              "
            />
            <p v-if="headerFormat === 'NONE'" class="text-xs text-n-slate-11">
              {{ t('WHATSAPP_TEMPLATES.BUILDER.HEADER.MEDIA_NOTE') }}
            </p>
          </div>

          <Input
            v-model="footer"
            :label="t('WHATSAPP_TEMPLATES.BUILDER.FOOTER_LABEL')"
            :placeholder="t('WHATSAPP_TEMPLATES.BUILDER.FOOTER_PLACEHOLDER')"
          />

          <TemplateButtonsEditor v-model:buttons="buttons" />
        </div>

        <!-- Live preview - same rendering approach as WhatsAppTemplateParser.vue -->
        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('WHATSAPP_TEMPLATES.BUILDER.PREVIEW.LABEL') }}
          </label>
          <div class="flex flex-col gap-3 p-4 rounded-lg bg-n-alpha-black2">
            <div
              v-if="headerFormat === 'TEXT' && headerText"
              class="text-sm font-semibold whitespace-pre-wrap text-n-slate-12"
            >
              {{ headerText }}
            </div>
            <div class="text-sm whitespace-pre-wrap text-n-slate-12">
              {{
                previewBody ||
                t('WHATSAPP_TEMPLATES.BUILDER.PREVIEW.EMPTY_BODY')
              }}
            </div>
            <div v-if="footer" class="text-xs text-n-slate-11">
              {{ footer }}
            </div>
            <div
              v-if="buttons.length"
              class="flex flex-col gap-1 pt-2 mt-1 border-t border-n-weak"
            >
              <div
                v-for="(btn, index) in buttons"
                :key="index"
                class="p-2 text-sm text-center rounded-md text-n-blue-11 bg-n-solid-2"
              >
                {{
                  btn.text ||
                  t('WHATSAPP_TEMPLATES.BUILDER.PREVIEW.UNNAMED_BUTTON')
                }}
              </div>
            </div>
          </div>
          <Label
            :label="t('WHATSAPP_TEMPLATES.BUILDER.PREVIEW.APPROXIMATE_BADGE')"
            color="amber"
            compact
          />
        </div>
      </div>

      <ul
        v-if="validationErrors.length"
        class="flex flex-col gap-1 p-3 text-sm rounded-md bg-n-ruby-9/10 text-n-ruby-9"
      >
        <li v-for="error in validationErrors" :key="error">{{ error }}</li>
      </ul>
      <p
        v-if="submitError"
        class="p-3 text-sm rounded-md bg-n-ruby-9/10 text-n-ruby-9"
      >
        {{ submitError }}
      </p>

      <div class="flex justify-end gap-2 pt-2">
        <Button
          :label="t('WHATSAPP_TEMPLATES.BUILDER.CANCEL')"
          variant="ghost"
          @click="dialogRef?.close()"
        />
        <Button
          :label="t('WHATSAPP_TEMPLATES.BUILDER.SUBMIT')"
          :is-loading="isSubmitting"
          :disabled="isSubmitting || !!validationErrors.length"
          @click="submit"
        />
      </div>
    </div>
  </Dialog>
</template>
